import { Link } from "react-router-dom";

interface ProcessStep {
  number: string;
  title: string;
  description: string;
  icon: string;
}

interface ProcessSectionProps {
  heading?: string;
  subtitle?: string;
  steps?: ProcessStep[];
}

const defaultSteps: ProcessStep[] = [
  {
    number: "01",
    icon: "ri-search-eye-line",
    title: "Free Inspection",
    description: "We assess every inch of damage and identify issues you may not even know exist — at zero cost to you.",
  },
  {
    number: "02",
    icon: "ri-file-list-3-line",
    title: "Honest Estimate",
    description: "No upsells, no pressure. A transparent, written estimate covering exactly what needs to be done.",
  },
  {
    number: "03",
    icon: "ri-hammer-line",
    title: "Expert Execution",
    description: "Our skilled crew works fast, clean, and precisely — using only premium materials rated for Colorado weather.",
  },
  {
    number: "04",
    icon: "ri-checkbox-circle-line",
    title: "Final Walkthrough",
    description: "We don't leave until you're satisfied. Final quality check and full walkthrough — our standard, not optional.",
  },
];

export default function ProcessSection({
  heading = "Simple, Transparent Roofing Process",
  subtitle = "From first call to final walkthrough — we make the entire process clear, fast, and stress-free.",
  steps = defaultSteps,
}: ProcessSectionProps) {
  return (
    <section className="bg-[#1A1A1A] py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="text-center mb-14">
          <span className="text-[#10B981] font-body font-600 text-sm tracking-widest uppercase mb-3 block">
            Our Process
          </span>
          <h2 className="font-headline font-800 text-white text-3xl lg:text-4xl xl:text-5xl mb-4 leading-tight">
            {heading}
          </h2>
          <p className="text-white/60 font-body text-base lg:text-lg max-w-2xl mx-auto">
            {subtitle}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-5 mb-14">
          {steps.map((step, idx) => (
            <div key={idx} className="relative group">
              {/* Connector line */}
              {idx < steps.length - 1 && (
                <div className="hidden lg:block absolute top-9 left-[calc(100%-12px)] w-6 h-px bg-[#10B981]/30 z-0" />
              )}
              <div className="relative z-10 p-7 rounded-lg bg-white/5 border border-white/10 group-hover:border-[#10B981]/50 group-hover:bg-white/8 transition-all duration-300 h-full">
                {/* Number */}
                <span className="font-headline font-900 text-[#10B981]/25 text-5xl leading-none block mb-4">
                  {step.number}
                </span>
                {/* Icon */}
                <div className="w-11 h-11 flex items-center justify-center rounded-lg bg-[#10B981]/15 border border-[#10B981]/20 mb-4">
                  <i className={`${step.icon} text-[#10B981] text-xl`}></i>
                </div>
                <h3 className="font-headline font-700 text-white text-lg mb-2">{step.title}</h3>
                <p className="text-white/55 font-body text-sm leading-relaxed">{step.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA Band */}
        <div className="rounded-lg bg-[#10B981] p-8 flex flex-col sm:flex-row items-center justify-between gap-5">
          <div>
            <p className="font-headline font-800 text-white text-xl lg:text-2xl mb-1">
              Ready to get started? It all begins with a free inspection.
            </p>
            <p className="text-white/80 font-body text-sm">No obligation. No pressure. Just honest answers about your roof.</p>
          </div>
          <div className="flex items-center gap-3 shrink-0">
            <Link
              to="/#contact"
              className="bg-white hover:bg-gray-50 text-[#1A1A1A] font-body font-700 text-sm px-7 py-3.5 rounded-md cursor-pointer whitespace-nowrap transition-colors"
            >
              Check My Roof Today
            </Link>
            <a
              href="tel:+17195550100"
              className="flex items-center gap-2 text-white font-body font-600 text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-fill text-white text-sm"></i>
              </div>
              Call Now
            </a>
          </div>
        </div>
      </div>
    </section>
  );
}
